/**
 * Hook personalizado que centraliza el estado y lógica del Radar.
 * Integra los hooks de nearbyUsers y geolocalización.
 * Maneja refresh, feedback, carga inicial y errores.
 * 
 * @param {Object} user - Usuario autenticado
 * @returns {Object} - Estado y funciones del radar
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNearbyUsers } from '../../../hooks/useNearbyUsers';
import { useUserLocation } from '../../../hooks/useUserLocation';
import { supabase } from '../../../lib/supabase';

export const useRadarState = (user) => {
  const queryClient = useQueryClient();
  
  // Estado local para refresh y mensajes de feedback
  const [refreshing, setRefreshing] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('Cargando...');
  
  // Hook de geolocalización
  const { 
    location, 
    error: locationError, 
    loading: locationLoading,
    updateCoordinates: requestLocation 
  } = useUserLocation(user);
  
  // Hook para obtener usuarios cercanos
  const { 
    data: nearbyUsers, 
    isLoading: loadingNearby, 
    error: nearbyError, 
    refetch 
  } = useNearbyUsers(user, location);

  /**
   * Procesa los datos de usuarios cercanos
   * Extrae información relevante para el componente
   * Memoiza para evitar re-renders innecesarios
   */
  const usersList = useMemo(() => nearbyUsers || [], [nearbyUsers]);
  // Move console.log to useEffect to avoid logging on every render
  useEffect(() => {
    console.log('[useRadarState] usersList:', usersList);
  }, [usersList]);
  const totalPersonas = useMemo(() => nearbyUsers?.length || 0, [nearbyUsers?.length]);
  const loadingUsers = loadingNearby || locationLoading;
  const nearbyUsersIds = useMemo(() => nearbyUsers?.map(user => user.id) || [], [nearbyUsers]);

  /**
   * Maneja la acción de refresh manual
   * Refresca los datos usando TanStack Query y solicita nueva ubicación
   */
  const handleRefresh = useCallback(async () => {
    console.log('[useRadarState] Iniciando refresh manual');
    setRefreshing(true);
    setFeedbackMessage('Actualizando...');

    try {
      // Solicitar nueva ubicación
      await requestLocation();
      
      // Refrescar query de usuarios cercanos
      await refetch();
      
      setFeedbackMessage('Datos actualizados');
      console.log('[useRadarState] Refresh completado exitosamente');
    } catch (error) {
      console.error('[useRadarState] Error en refresh:', error);
      setFeedbackMessage('Error al actualizar');
    } finally {
      setRefreshing(false);
    }
  }, [refetch, requestLocation]);

  /**
   * Función de carga inicial de datos
   * Usa refetch de TanStack Query para consistencia
   */
  const loadRadarData = useCallback(async () => {
    console.log('[useRadarState] Cargando datos iniciales del radar');
    try {
      await refetch();
      console.log('[useRadarState] Datos iniciales cargados exitosamente');
    } catch (error) {
      console.error('[useRadarState] Error cargando datos iniciales:', error);
      setFeedbackMessage('Error al cargar datos');
    }
  }, [refetch]);

  /**
   * Efecto para actualizar mensaje de feedback basado en el estado
   * Only depends on length to avoid re-render cycles when array reference changes
   */
  useEffect(() => {
    if (locationError) {
      setFeedbackMessage(`Error de ubicación: ${locationError}`);
    } else if (nearbyError) {
      setFeedbackMessage(`Error: ${nearbyError.message}`);
    } else if (loadingUsers) {
      setFeedbackMessage('Cargando usuarios cercanos...');
    } else if (usersList.length === 0 && !loadingUsers) {
      setFeedbackMessage('No hay usuarios cercanos');
    } else {
      setFeedbackMessage('Datos cargados correctamente');
    }
  }, [locationError, nearbyError, loadingUsers, usersList.length]);

  /**
   * Efecto para suscribirse a cambios en tiempo real en la tabla profiles
   * Solo invalida caché de avatares, NO invalida usuarios cercanos para evitar re-renders excesivos
   * Los usuarios cercanos se actualizan manualmente con el botón reload o cada 1 hora
   */
  useEffect(() => {
    if (!user?.id) return;

    console.log('[useRadarState] Configurando suscripción Realtime a profiles...');

    const channel = supabase
      .channel('nearby-users-changes')
      .on('postgres_changes', {
        event: '*', // Escuchar INSERT, UPDATE, DELETE
        schema: 'public',
        table: 'profiles',
        filter: `id=neq.${user.id}` // Excluir al usuario actual
      }, (payload) => {
        console.log('[useRadarState] Cambio detectado en profiles:', payload);

        // Solo invalidar caché de avatares si cambió la imagen
        // NO invalidar usuarios cercanos para evitar re-renders excesivos
        if (payload.eventType === 'UPDATE' && payload.new?.user_image !== payload.old?.user_image) {
          queryClient.invalidateQueries(['avatar', payload.new.id]);
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('[useRadarState] Realtime conectado para nearby users');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[useRadarState] Error en canal Realtime');
        } else if (status === 'TIMED_OUT') {
          console.warn('[useRadarState] Timeout en canal Realtime');
        }
      });

    // Cleanup: remover suscripción al desmontar
    return () => {
      console.log('[useRadarState] Limpiando suscripción Realtime...');
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  return {
    // Estado
    loadingUsers,
    refreshing,
    usersList,
    feedbackMessage,
    totalPersonas,
    nearbyUsersIds,
    locationError,
    location,
    
    // Acciones
    loadRadarData,
    handleRefresh,
    requestLocation,
  };
};
